/**
 * A tela 1 com o caso de entrada aberto (E144.F3, GUS-135, ADR-0098; E165.F1.T03, HTML 6.8), na
 * ordem do molde: a identificação (processo, pasta e CPF, com o prazo e a urgência recolhidos), o
 * tipo de exame (fixo) e as observações. "Continuar" grava no processo o que mudou na identificação
 * e no pedido e segue.
 *
 * E177.F4 (GUS-253, ADR-0130): os arquivos saíram desta tela — vão na "Contratos digitais", um bloco
 * por CCB, para onde o "Continuar" leva. Na abertura do cliente a identificação é obrigatória:
 * incompleta, ela segura o "Continuar".
 */
import { useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';

import { ptBR } from '@/i18n/pt-BR';
import {
  identificacaoDoProcesso,
  PEDIDO_VAZIO,
  pedidoDoProcesso,
  type FormularioDoEnvio,
  type ServicoDaAbertura,
} from '@/lib/abertura';
import { getCase, type CaseRecordData } from '@/lib/cases';
import { lerProcesso, type ProcessoData } from '@/lib/processos';

import { CartoesDeServico } from './CartoesDeServico';
import { FormularioDaIdentificacao } from './FormularioDaIdentificacao';
import { ObservacoesDoPedido } from './PedidoDoCliente';
import { RodapeDoEnvio } from './RodapeDoEnvio';
import { errosDoFormulario, gravarPedidoNoProcesso } from './regrasDoEnvio';

const t = ptBR.abertura;

export interface EnviarComCasoProps {
  client: AxiosInstance;
  caseId: string;
  /** E177.F4: para a tela "Contratos digitais". */
  onContinuar: () => void;
  onVoltar: () => void;
  /** O caso não é da eletrônica: a página o leva ao envio de sempre. */
  onOutroServico: (caso: CaseRecordData) => void;
  /** E165.F1.T03: a abertura do cliente, com a identificação obrigatória. */
  doCliente?: boolean;
}

interface Carregado {
  caso: CaseRecordData;
  processo: ProcessoData | null;
  formulario: FormularioDoEnvio;
}

async function carregar(client: AxiosInstance, caseId: string): Promise<Carregado> {
  const caso = await getCase(client, caseId);
  const processo =
    caso.processo_id !== null && caso.processo_id !== undefined
      ? (await lerProcesso(client, caso.processo_id)).processo
      : null;
  const formulario: FormularioDoEnvio = {
    identificacao: identificacaoDoProcesso(processo),
    pedido: processo !== null ? pedidoDoProcesso(processo) : PEDIDO_VAZIO,
  };
  return { caso, processo, formulario };
}

export function EnviarComCaso(props: EnviarComCasoProps): JSX.Element {
  const { client, caseId, doCliente = false, onOutroServico } = props;
  const [carregado, setCarregado] = useState<Carregado | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [formulario, setFormulario] = useState<FormularioDoEnvio | null>(null);
  const [tentou, setTentou] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let ativo = true;
    carregar(client, caseId).then(
      (dados) => {
        if (!ativo) return;
        if (dados.caso.modalidade !== 'eletronica') onOutroServico(dados.caso);
        setCarregado(dados);
        setFormulario(dados.formulario);
      },
      () => ativo && setFalhou(true),
    );
    return () => {
      ativo = false;
    };
  }, [client, caseId, onOutroServico]);

  if (carregado === null || formulario === null) {
    return falhou ? (
      <p role="alert" className="text-body-sm text-danger" data-testid="abertura-enviar-erro">
        {t.arquivos.erroAoCarregar}
      </p>
    ) : (
      <p className="text-body-sm text-fg-muted">{t.arquivos.carregando}</p>
    );
  }
  const erros = errosDoFormulario(formulario, doCliente);

  async function continuar(): Promise<void> {
    if (carregado === null || formulario === null) return;
    setTentou(true);
    setErro(false);
    if (erros.algum) return;
    setGravando(true);
    try {
      await gravarPedidoNoProcesso(
        client,
        carregado.caso,
        formulario,
        carregado.processo ?? undefined,
      );
      props.onContinuar();
    } catch {
      setErro(true);
      setGravando(false);
    }
  }

  const mudarPedido = (pedido: FormularioDoEnvio['pedido']): void =>
    setFormulario({ ...formulario, pedido });

  return (
    <form
      onSubmit={(evt) => {
        evt.preventDefault();
        void continuar();
      }}
      className="flex flex-col gap-5"
      noValidate
      data-testid="abertura-enviar"
    >
      <FormularioDaIdentificacao
        valor={formulario}
        onChange={setFormulario}
        erros={erros}
        tentou={tentou}
        obrigatoria={doCliente}
        disabled={gravando}
      />
      <CartoesDeServico
        escolhido={(carregado.caso.modalidade ?? 'eletronica') as ServicoDaAbertura}
        onEscolher={() => undefined}
        disabled
      />
      <ObservacoesDoPedido valor={formulario.pedido} onChange={mudarPedido} disabled={gravando} />
      {erro ? (
        <p role="alert" className="text-body-sm text-danger" data-testid="abertura-erro">
          {t.erroAoGravarPedido}
        </p>
      ) : null}
      <RodapeDoEnvio
        abrindo={gravando}
        travado={doCliente && erros.identificacaoIncompleta}
        onVoltar={props.onVoltar}
      />
    </form>
  );
}
